/**
 * @prettier
 */

// React Packages
import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { bindActionCreators } from "redux";

// Actions
import { clearError } from "../../state/actions/navigatorActions";
import { completeStage } from "../../state/actions/submissionStateActions";

// Services
import apiService from "../../services/api/api";
import logService from "../../services/shared/logService";
import DataDogService from "../../services/shared/datadogService";
import documentService from "../../services/shared/documentService";
import {
    getCompanyName,
    isSubmissionComplete,
    sanitiseImage,
} from "../../services/shared/helpers";
import { regenerateSession } from "../../services/shared/authenticationService";

//  Components
import Header from "../../components/Header";
import Navigator from "../../components/Navigator";
import CustomButton from "../../components/CustomButton";
import StageComplete from "../../components/Messages/StageComplete";
import SubmissionFlowStepper from "../../components/SubmissionFlowStepper";

// Images
import livenessSvg from "../../assets/img/liveness.svg";
import { LoadingStateAnimation } from "../../components/ProcessingLayout";

// Config
import { imageAlt } from "../../config/accessabilityRules";
import { ACTION_LABELS } from "../../config/dataDogActionLabels";

const ACTION = ACTION_LABELS.captureSelfie;

class CaptureSelfieFallback extends Component {
    constructor(props) {
        super(props);
        this.primaryFocusRef = React.createRef();
        this.state = {
            loading: false,
            showInstructions: true,
            imageLoaded: false,
            livenessImage: null,
            refreshAttempted: false,
            navigation: {
                action: "load",
                props: null,
            },
        };
        this.captureLivenessImage = this.captureLivenessImage.bind(this);
        this.startCamera = this.startCamera.bind(this);
    }

    componentDidMount() {
        // Sets focus to primary heading on first render
        if (this.primaryFocusRef && this.primaryFocusRef.current) {
            this.primaryFocusRef.current.focus();
        }

        // Sets document title
        const { t } = this.props;
        document.title = t("idpal_doc_title_liveness");
    }

    componentDidUpdate(prevProps, prevState) {
        // Sets document title
        const { t } = this.props;
        if (prevState.livenessError !== this.state.livenessError) {
            document.title = this.state.livenessError
                ? t("idpal_doc_title_liveness_error")
                : t("idpal_doc_title_liveness");
        }

        if (this.state.imageLoaded !== prevState.imageLoaded) {
            if (this.state.livenessImage !== null) {
                // For reusable profiles, use legacy endpoint
                if (this.props.context !== "initial") {
                    this.submitLivenessImage(this.state.livenessImage);

                    // Otherwise send binary liveness to new endpoint
                } else {
                    this.submitLiveness(
                        documentService.urlToBlob(this.state.livenessImage),
                        "image"
                    );
                }
            }
        }
    }

    // Open camera input
    startCamera() {
        document.getElementById("selfieInput").click();
        DataDogService.log("Starting camera with ID-Pal Liveness.");
        logService.log("Starting camera with ID-Pal Liveness.");
    }

    // Capture liveness image from native camera input
    captureLivenessImage() {
        const SELF = this;
        DataDogService.log("Capturing Image with ID-Pal Liveness.");
        document
            .getElementById("selfieInput")
            .addEventListener("change", function () {
                try {
                    if (this.files && this.files[0]) {
                        const FILE_READER = new FileReader();
                        FILE_READER.readAsDataURL(this.files[0]);
                        FILE_READER.addEventListener("load", function (event) {
                            try {
                                const IMAGE = new Image();
                                IMAGE.src = event.target.result;
                                IMAGE.onload = function () {
                                    // Draw the image to canvas once it is captured
                                    SELF.setState({
                                        livenessImage: SELF.drawToCanvas(this),
                                    });
                                    SELF.setState({ imageLoaded: true });
                                    SELF.setState({ showInstructions: false });
                                };
                            } catch (error) {
                                DataDogService.createError(
                                    "Error loading image with ID-Pal Liveness."
                                );
                                logService.error(
                                    `${error.name}: ${error.message}`
                                );
                            }
                        });
                    } else {
                        DataDogService.createError(
                            "No image uploaded with ID-Pal Liveness."
                        );
                        logService.error(
                            "No image uploaded with ID-Pal Liveness"
                        );
                    }
                } catch (error) {
                    DataDogService.createError(
                        "Error capturing image with ID-Pal Liveness."
                    );
                    logService.error(`${error.name}: ${error.message}`);
                }
            });
    }

    // Draw captured image to canvas to retain image orientation on Android Phones
    drawToCanvas(image) {
        const hiddenCanvas = document.querySelector("#hiddenCanvasSelfie");
        hiddenCanvas.width = image.width;
        hiddenCanvas.height = image.height;

        const ctx = hiddenCanvas.getContext("2d");
        ctx.mozImageSmoothingEnabled = false;
        ctx.webkitImageSmoothingEnabled = false;
        ctx.msImageSmoothingEnabled = false;
        ctx.imageSmoothingEnabled = false;
        ctx.drawImage(image, 0, 0, image.width, image.height);

        const canvasImage = hiddenCanvas.toDataURL("image/jpeg");
        return canvasImage;
    }

    // Send Liveness Binary to Liveness endpoint for validation
    submitLiveness(binary, type) {
        clearError(this);
        this.setState({ loading: true });

        apiService
            .postPasssiveLiveness(binary, type)
            .then(response => {
                DataDogService.log("ID-Pal Liveness successfully sent.");
                this.props.completeStage("passive_liveness");
                this.setState({
                    navigation: {
                        action: "next",
                    },
                });
            })
            .catch(error => {
                if (error.status === 403 && !this.state.refreshAttempted) {
                    DataDogService.createError(
                        "ID-Pal Liveness error occurred, session expired."
                    );
                    // Session Expired
                    regenerateSession(
                        this.submitLiveness,
                        [binary, type],
                        this
                    );
                } else if (error.status === 420) {
                    // Invalid verification
                    DataDogService.log(
                        "ID-Pal Liveness successfully sent, invalid result. Continuing."
                    );
                    this.props.completeStage("passive_liveness");
                    this.setState({
                        navigation: {
                            action: "next",
                        },
                    });
                } else if (error.status === 429) {
                    // Too many previous attempts
                    DataDogService.log(
                        "ID-Pal Liveness successfully sent, too many previous attempts. Continuing. "
                    );
                    this.props.completeStage("passive_liveness");
                    this.setState({
                        navigation: {
                            action: "next",
                        },
                    });
                } else {
                    // Actual error
                    DataDogService.createError(
                        "Unable to send ID-Pal liveness for verification."
                    );
                    this.setState({ loading: false });
                    logService.error(error);
                    this.setState({
                        navigation: {
                            action: "error",
                            props: {
                                retryAction: () =>
                                    this.submitLiveness(binary, type),
                                error: error,
                            },
                        },
                    });
                }
            });
    }

    // Send Base64 Image to API for validation - LEGACY
    submitLivenessImage(base64Image) {
        const image = sanitiseImage(base64Image);

        clearError(this);
        this.setState({ loading: true });

        // Send passive liveness selfie base64Image to the server for processing
        apiService
            .getPassiveLivenessResult(image)
            .then(response => {
                this.props.dispatch({
                    payload: response,
                    type: "@@acuant/ADD_FACE_LIVENESS_DATA",
                });
                this.props.completeStage("passive_liveness");
                DataDogService.log(
                    "ID-Pal Liveness successfully sent for verification at legacy endpoint"
                );
                this.setState({
                    navigation: {
                        action: "next",
                    },
                });
            })
            .catch(error => {
                DataDogService.createError(
                    "ID-Pal Liveness for verification at legacy endpoint."
                );
                this.setState({ loading: false });
                logService.error(error);
                this.setState({
                    navigation: {
                        action: "error",
                        props: {
                            retryAction: () =>
                                this.submitLivenessImage(base64Image),
                        },
                    },
                });
            });
    }

    render() {
        const { t } = this.props;

        if (isSubmissionComplete()) {
            return (
                <Fragment>
                    <Header />
                    <StageComplete
                        message={t("idpal_your_submission_is_complete", {
                            company: getCompanyName(this.props.companyName),
                        })}
                        hideContinue={true}
                        hideButton={true}
                    />
                </Fragment>
            );
        }

        return (
            <div className="root-layout-container capture-selfie-page">
                {/* Component starts proper */}
                <Header />
                <SubmissionFlowStepper />

                {/* Loading state */}
                {this.state.loading && (
                    <div className="instructions-content-wrapper">
                        <LoadingStateAnimation
                            brandingColor={`#${this.props.branding.primary_color}`}
                        />
                    </div>
                )}

                {/* Liveness capture instructions */}
                {this.state.showInstructions && !this.state.loading && (
                    <div className="instructions-content-wrapper">
                        <h1
                            className="instructions-title"
                            ref={this.primaryFocusRef}
                            tabIndex={-1}
                        >
                            {t("idpal_unable_to_access_auto_capture")}
                        </h1>

                        <div className="selfie-instructions-image">
                            <div className="image-wrapper">
                                <img
                                    alt={imageAlt.selfieInstructions}
                                    className="capture"
                                    src={livenessSvg}
                                />
                            </div>
                        </div>

                        <p className="instructions-content">
                            {t("idpal_we_were_unable_to_access_auto_capture")}
                            <br />
                            {t("idpal_take_photo_of_face")}
                        </p>

                        <div className="capture-selfie-page-cta-container">
                            <CustomButton
                                id={"capture"}
                                className="btn"
                                label={t("idpal_open_camera")}
                                handleClick={this.startCamera.bind(this)}
                                actionDataLabel={ACTION.captureLivenessButton}
                            />
                        </div>
                    </div>
                )}

                {/* Hidden inputs*/}
                <input
                    className="hidden"
                    type="file"
                    id="selfieInput"
                    accept="image/*"
                    capture="user"
                    onClick={() => this.captureLivenessImage()}
                />
                <canvas className="hidden" id="hiddenCanvasSelfie"></canvas>
                <Navigator
                    page={"passive_liveness"}
                    action={this.state.navigation.action}
                    propsToPass={this.state.navigation.props}
                />
            </div>
        );
    }
}

function mapDispatchToProps(dispatch) {
    const actions = bindActionCreators({ completeStage }, dispatch);
    return { ...actions, dispatch };
}

function mapStateToProps(state) {
    return {
        isDocSubmitted: state.config.isDocSubmitted,
        docCompleted:
            state.submissionState.submissionState.document_upload.completed,
        poaCompleted:
            state.submissionState.submissionState.poa_upload.completed,
        isPoaEnabled: state.submissionState.screens.poa_upload.enabled,
        context: state.submissionState.screens.passive_liveness.context,
        isPoaDocSubmitted: state.config.isPoaDocSubmitted,
        companyName: state.config.profile.data.company_branding.name,
        branding: state.config.profile.data.company_branding,
    };
}

export default withTranslation("translation")(
    connect(mapStateToProps, mapDispatchToProps)(CaptureSelfieFallback)
);
